import React, { CSSProperties, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Point } from '../../types/point';
import { PointMinimap } from './PointMinimap';
import { ColorPickerModal } from '../ColorPickerModal';

interface PointCardProps {
  point: Point;
  areaPoints?: Point[];
  onPlay: (id: number) => void;
  onDelete: (id: number) => void;
  onRename: (id: number, newName: string) => void;
  onColorChange: (id: number, color: string) => void;
  style?: CSSProperties;
}

export const PointCard: React.FC<PointCardProps> = ({
  point,
  areaPoints = [],
  onPlay,
  onDelete,
  onRename,
  onColorChange,
  style,
}) => {
  const { t } = useTranslation();

  const [renameOpen, setRenameOpen] = useState(false);
  const [colorOpen, setColorOpen]   = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [tempName, setTempName]     = useState('');

  const colorHex       = point.color || '#FFFFFF';
  const isDefaultColor = colorHex.toUpperCase() === '#FFFFFF';

  useEffect(() => {
    if (!renameOpen && !deleteOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setRenameOpen(false); setDeleteOpen(false); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [renameOpen, deleteOpen]);

  const openRename = () => { setTempName(point.name); setRenameOpen(true); };
  const submitRename = () => { if (tempName.trim()) onRename(point.id, tempName.trim()); setRenameOpen(false); };
  const submitDelete = () => { onDelete(point.id); setDeleteOpen(false); };

  return (
    <div className="point-card" style={style}>

      {/* ── Minimap ─────────────────────────────────────────────────────── */}
      <div className="point-card__minimap">
        <PointMinimap center={point} areaPoints={areaPoints} size={90} />
        <span className="point-card__minimap-coords">{point.x}, {point.y}</span>
        <div className="point-card__minimap-overlay" onClick={() => onPlay(point.id)}>
          {t('pointCard.play')}
        </div>
      </div>

      {/* ── Body ────────────────────────────────────────────────────────── */}
      <div className="point-card__body">
        <h3 className="point-card__name">{point.name}</h3>
        <div className="point-card__meta">
          <div className="point-card__meta-item">
            <span className="point-card__meta-label">X</span>
            <span className="point-card__meta-value">{point.x}</span>
          </div>
          <div className="point-card__meta-item">
            <span className="point-card__meta-label">Y</span>
            <span className="point-card__meta-value">{point.y}</span>
          </div>
          <div className="point-card__meta-item">
            <span
              className="point-card__meta-color-swatch"
              style={{ backgroundColor: colorHex }}
              title={colorHex}
            />
            <span className="point-card__meta-value">
              {isDefaultColor ? t('pointCard.defaultColor') : colorHex.toUpperCase()}
            </span>
          </div>
        </div>
      </div>

      {/* ── Actions ─────────────────────────────────────────────────────── */}
      <div className="point-card__actions">
        <button className="point-card__action-btn point-card__action-btn--play" onClick={() => onPlay(point.id)}>
          {t('pointCard.play')}
        </button>
        <button className="point-card__action-btn point-card__action-btn--rename" onClick={openRename}>
          {t('pointCard.rename')}
        </button>
        <button className="point-card__action-btn point-card__action-btn--color" onClick={() => setColorOpen(true)}>
          {t('pointCard.color')}
        </button>
        <button className="point-card__action-btn point-card__action-btn--delete" onClick={() => setDeleteOpen(true)}>
          {t('pointCard.del')}
        </button>
      </div>

      {/* ── Color picker ────────────────────────────────────────────────── */}
      <ColorPickerModal
        open={colorOpen}
        title={t('pointCard.colorTitle')}
        currentColor={colorHex}
        onClose={() => setColorOpen(false)}
        onApply={color => onColorChange(point.id, color)}
      />

      {/* ── Rename modal ────────────────────────────────────────────────── */}
      {renameOpen && ReactDOM.createPortal(
        <div className="pixel-modal-overlay" onClick={() => setRenameOpen(false)}>
          <div className="pixel-modal" onClick={e => e.stopPropagation()}>
            <h3 className="pixel-modal__title">{t('pointCard.renameTitle')}</h3>
            <form onSubmit={e => { e.preventDefault(); submitRename(); }}>
              <input
                className="pixel-modal__input"
                type="text"
                value={tempName}
                onChange={e => setTempName(e.target.value)}
                placeholder={point.name}
                maxLength={50}
                autoFocus
              />
              <div className="pixel-modal__actions">
                <button type="button" className="pixel-modal__btn" onClick={() => setRenameOpen(false)}>
                  {t('common.cancel')}
                </button>
                <button type="submit" className="pixel-modal__btn pixel-modal__btn--primary">
                  {t('pointCard.saveBtn')}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body,
      )}

      {/* ── Delete modal ─────────────────────────────────────────────────── */}
      {deleteOpen && ReactDOM.createPortal(
        <div className="pixel-modal-overlay" onClick={() => setDeleteOpen(false)}>
          <div className="pixel-modal" onClick={e => e.stopPropagation()}>
            <h3 className="pixel-modal__title">{t('pointCard.deleteTitle')}</h3>
            <p className="pixel-modal__hint">{t('pointCard.deleteConfirm', { name: point.name })}</p>
            <div className="pixel-modal__actions">
              <button type="button" className="pixel-modal__btn" onClick={() => setDeleteOpen(false)}>
                {t('common.cancel')}
              </button>
              <button type="button" className="pixel-modal__btn pixel-modal__btn--danger" onClick={submitDelete}>
                {t('pointCard.deleteBtn')}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
};
